import React, { useEffect, useState } from "react";
import axios from "axios";
import "./LandingPage.css";

const API = "https://ikaze-thouse-backend.vercel.app/api/houses";
const BOOKING_API = "https://ikaze-thouse-backend.vercel.app/api/Salebooking";

function LandingPage() {
  const [houses, setHouses] = useState([]);
  const [bookedHouseIds, setBookedHouseIds] = useState([]); // Tracks successfully booked house IDs
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [bookingForm, setBookingForm] = useState({
    house_id: "",
    house_title: "",
    price_usd: "",
    booking_amount: "", 
    location: "",
    bedrooms: "",
    bathrooms: "",
    customer_name: "",
    email: "",
    phone: "",
    national_id: "",
    address: "",
    payment_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Concurrently load houses and existing bookings to cross-reference status
      const [housesRes, bookingsRes] = await Promise.all([
        axios.get(API),
        axios.get(BOOKING_API).catch(() => ({ data: [] })) // Fallback gracefully if endpoint differs
      ]);

      const housesData = housesRes.data || [];
      setHouses(housesData);

      // Parse active bookings (Filter out bookings with status explicitly marked as 'Reject' or 'Rejected')
      if (Array.isArray(bookingsRes.data)) {
        const activeBookedIds = bookingsRes.data
          .filter(b => b.status !== "Reject" && b.status !== "Rejected" && b.status !== "reject")
          .map(b => String(b.house_id));
        setBookedHouseIds(activeBookedIds);
      }
    } catch (error) {
      console.error("Initialization Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const openBookingForm = (house) => {
    // Prevent interaction if house is already booked
    if (bookedHouseIds.includes(String(house.id))) return;

    const bookingFee = Number(house.price_usd || 0) * 0.25;

    setBookingForm({
      house_id: house.id,
      house_title: house.title,
      price_usd: house.price_usd,
      booking_amount: bookingFee, 
      location: house.location,
      bedrooms: house.bedrooms,
      bathrooms: house.bathrooms,
      customer_name: "",
      email: "",
      phone: "",
      national_id: "",
      address: "",
      payment_date: "",
      notes: ""
    });

    setShowBooking(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!bookingForm.customer_name || !bookingForm.email || !bookingForm.phone) {
      alert("Please enter your name, email, and phone number.");
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(BOOKING_API, bookingForm);
      
      alert("Your booking request has been sent successfully!");
      
      // Add this house ID to booked array to change UI state instantly
      setBookedHouseIds(prev => [...prev, String(bookingForm.house_id)]);
      
      setShowBooking(false);
      setBookingForm({
        house_id: "", house_title: "", price_usd: "", booking_amount: "",
        location: "", bedrooms: "", bathrooms: "", customer_name: "",
        email: "", phone: "", national_id: "", address: "", payment_date: "", notes: ""
      });
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please check your information and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-premium">
      
      {/* EDITORIAL NAVIGATION / BRAND HEADER */}
      <header className="brand-header">
        <div className="brand-nav-container">
          {/* <span className="brand-logo">GKM <span className="logo-sub">INVEST</span></span> */}
          
          <nav className="brand-menu">
            {/* <a href="#properties" className="active-link">Available Houses</a>             */}
            <a href="#investment-philosophy">Why Choose Us</a>
          </nav>
          <nav className="brand-menu">
            {/* <a href="#properties" className="active-link">Available Houses</a>             */}
            <a href="#acquisition-process">How to Buy</a>
          </nav>
        </div>
      </header>

      {/* LUXURY SLATE HERO */}
      <section className="hero-section">
        <div className="hero-content">
          <p className="hero-subtitle">PREMIUM HOMES FOR SALE</p>
          <h1 className="hero-title">Beautiful Houses <br />Built to Last for Generations.</h1>
          <p className="hero-lead">
            Find luxury homes and ready-to-move-in private villas in the best areas of Rwanda. Own a beautiful property in one of the fastest-growing locations in Africa.
          </p>
          <div className="hero-cta-wrapper">
            <a href="#properties" className="hero-cta-btn">View All Houses</a>
            <a href="#investment-philosophy" className="hero-secondary-btn">Market Info</a>
          </div>
        </div>
      </section>

      {/* INVESTMENT ADVANTAGE INFOGRAPHIC STRIP */}
      <section id="investment-philosophy" className="investment-metrics-strip">
        <div className="metrics-container">
          <div className="metric-card">
            <h4>100% Full Ownership</h4>
            <p>You get complete legal ownership and rights to the land and property.</p>
          </div>
          <div className="metric-card">
            <h4>Best Locations Only</h4>
            <p>We pick houses in great areas where property values grow quickly over time.</p>
          </div>
          <div className="metric-card">
            <h4>Clean & Verified Titles</h4>
            <p>Every property is fully checked by our lawyers before we list it for sale.</p>
          </div>
        </div>
      </section>

      {/* PROPERTY PORTFOLIO GALLERY */}
      <main id="properties" className="portfolio-section">
        <div className="section-intro">
          <span className="curated-label">HOUSES FOR SALE</span>
          <h2>Our Collection</h2>
          <p className="section-subtext">
            Choose a house below to see details and start your easy purchase process securely.
          </p>
        </div>

        {loading ? (
          <div className="premium-loader">
            <div className="line-pulse"></div>
            <p>Loading available houses...</p>
          </div>
        ) : (
          <div className="portfolio-grid">
            {houses.map((house) => {
              const isAlreadyBooked = bookedHouseIds.includes(String(house.id));
              
              return (
                <article key={house.id} className={`portfolio-card ${isAlreadyBooked ? "card-booked-dim" : ""}`}>
                  <div className="card-media-box">
                    <img
                      src={house.image_url || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80"}
                      alt={house.title}
                    />
                    <div className="card-location-tag">
                      {isAlreadyBooked ? `SOLD OUT • ${house.location}` : house.location}
                    </div>
                  </div>

                  <div className="card-details-box">
                    <span className="asset-tag">Residential House</span>
                    <h3 className="property-heading">{house.title}</h3>
                    
                    <div className="property-valuation">
                      {Number(house.price_usd || 0).toLocaleString()} <span className="unit">RWF</span>
                    </div>
                    
                    <div className="property-metrics-row">
                      <span><strong>{house.bedrooms}</strong> Bedrooms</span>
                      <span className="divider">|</span>
                      <span><strong>{house.bathrooms}</strong> Bathrooms</span>
                    </div>
                    
                    <p className="property-excerpt">
                      {house.description || "A beautiful, modern home made with high-quality materials, spacious rooms, and great views."}
                    </p>
                    
                    <button
                      className={`portfolio-action-btn ${isAlreadyBooked ? "btn-status-booked" : ""}`}
                      onClick={() => openBookingForm(house)}
                      disabled={isAlreadyBooked}
                    >
                      {isAlreadyBooked ? "Sold Out" : "Book This House"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* ACQUISITION PROCESS STEPPING */}
      <section id="acquisition-process" className="process-guide-section">
        <div className="process-container">
          <span className="curated-label">HOW IT WORKS</span>
          <h2>3 Steps to Own Your Home</h2>
          
          <div className="process-steps-grid">
            <div className="step-item">
              <span className="step-num">01</span>
              <h3>Fill out the Form</h3>
              <p>Fill out the quick booking form on our website to hold your spot instantly.</p>
            </div>
            <div className="step-item">
              <span className="step-num">02</span>
              <h3>Pay the Deposit</h3>
              <p>Pay a 25% down payment to take the house off the market so no one else can buy it.</p>
            </div>
            <div className="step-item">
              <span className="step-num">03</span>
              <h3>Sign the Papers</h3>
              <p>Finish the official title deed transfer and paperwork with our private lawyers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* HIGH-END MINIMAL BOOKING MODAL */}
      {showBooking && (
        <div className="modal-overlay-backdrop">
          <div className="modal-viewport-card">
            
            <div className="modal-sidebar-decor">
              <h3>Secure Booking</h3>
              <p>Your details are safely processed directly through our main real estate office.</p>
              <div className="security-guarantee-note">
                <p>✓ Legal Title Transfer Guaranteed</p>
                <p>✓ All Background Checks Done</p>
              </div>
            </div>

            <div className="modal-main-workspace">
              <div className="workspace-header">
                <h2>House Booking Form</h2>
                <button className="raw-close-btn" onClick={() => !isSubmitting && setShowBooking(false)}>×</button>
              </div>

              <form onSubmit={submitBooking} className="workspace-form">
                
                {/* ACCOUNT STATS SUB-BOX */}
                <div className="invoice-preview-box">
                  <div className="invoice-row">
                    <span>Selected House:</span>
                    <strong>{bookingForm.house_title}</strong>
                  </div>
                  <div className="invoice-row">
                    <span>Total House Price:</span>
                    <span>{Number(bookingForm.price_usd || 0).toLocaleString()} RWF</span>
                  </div>
                  <div className="invoice-row highlighted-row">
                    <span>Required Deposit (25%):</span>
                    <strong>{Number(bookingForm.booking_amount || 0).toLocaleString()} RWF</strong>
                  </div>
                </div>

                {/* FIELDS ZONE */}
                <div className="workspace-fields-grid">
                  <div className="field-block">
                    <label>Your Full Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. danny"
                      required
                      value={bookingForm.customer_name}
                      onChange={(e) => setBookingForm({ ...bookingForm, customer_name: e.target.value })}
                    />
                  </div>

                  <div className="field-block">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      placeholder="name@example.com"
                      required
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    />
                  </div>

                  <div className="field-block">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      placeholder="+250..."
                      required
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    />
                  </div>

                  <div className="field-block">
                    <label>National ID / Passport Number</label>
                    <input
                      type="text"
                      placeholder="Enter your ID or passport number"
                      value={bookingForm.national_id}
                      onChange={(e) => setBookingForm({ ...bookingForm, national_id: e.target.value })}
                    />
                  </div>

                  <div className="field-block full-width">
                    <label>Current Home Address</label>
                    <input
                      type="text"
                      placeholder="City, Country"
                      value={bookingForm.address}
                      onChange={(e) => setBookingForm({ ...bookingForm, address: e.target.value })}
                    />
                  </div>

                  <div className="field-block">
                    <label>Expected Payment Date</label>
                    <input
                      type="date"
                      value={bookingForm.payment_date}
                      onChange={(e) => setBookingForm({ ...bookingForm, payment_date: e.target.value })}
                    />
                  </div>

                  <div className="field-block full-width">
                    <label>Special Notes or Requests</label>
                    <textarea
                      rows="2"
                      placeholder="Type any special requests or questions here..."
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                    />
                  </div>
                </div>

                <div className="workspace-actions">
                  <button 
                    type="button" 
                    className="cancel-action-btn"
                    onClick={() => setShowBooking(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-action-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending Request..." : "Confirm Booking Request"}
                  </button>
                </div>

              </form>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;